import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Container, Input, Label, Row, UncontrolledTooltip, Button } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Trash } from 'react-feather';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import { getAllUsers as getAllUsersAction, toggleUserStatus as toggleUserStatusAction, deleteUsers as deleteUsersAction, toggleCrmFeature as toggleCrmFeatureAction, resetUserPassword as resetUserPasswordAction, toggleUserAdmin as toggleUserAdminAction } from '../ReduxToolkit/Reducers/userManagement';
import ConfirmationModal from '../Components/ConfirmationModal';
import ResetPasswordModal from './ResetPasswordModal';

function UserManagementUsersTab({ getAllUsers, allUsers, totalUsers, isLoading, toggleUserStatus, deleteUsers, toggleCrmFeature, resetUserPassword, toggleUserAdmin, organizationName, embedded = false }) {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState(organizationName?.trim() || '');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [selectedRowsData, setSelectedRowsData] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [singleDeleteUser, setSingleDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [userForReset, setUserForReset] = useState(null);

  useEffect(() => {
    if (organizationName) {
      setSearchText(organizationName.trim());
      setPage(1);
    }
  }, [organizationName]);

  useEffect(() => {
    if (page) {
      _getTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, itemsPerPage]);

  useEffect(() => {
    if (allUsers[page]) {
      setTableData(allUsers[page]);
    }
  }, [allUsers, page]);

  const _getTableData = async (hideLoader = false) => {
    await getAllUsers(page, searchText, itemsPerPage, hideLoader);
  };

  const _searchUsers = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(_searchUsers, 1000), []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceFn(e.target.value);
  };

  const handleItemPerPageSelect = async (count) => {
    setPage(1);
    setItemsPerPage(count);
  };

  const handleUserToggle = async (user) => {
    // Optimistically update the UI
    setTableData((prevData) =>
      prevData.map((item) =>
        item.id === user.id
          ? { ...item, active: !item.active }
          : item
      )
    );

    const result = await toggleUserStatus(user.id, user.active);

    // Refresh data after API call to ensure consistency
    if (result?.statusCode === 200) {
      _getTableData(true);
    } else {
      // Revert optimistic update on error
      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === user.id
            ? { ...item, active: !item.active }
            : item
        )
      );
    }
  };

  const handleCrmToggle = async (user) => {
    // Optimistically update the UI
    setTableData((prevData) =>
      prevData.map((item) =>
        item.id === user.id
          ? { ...item, crmOpen: !item.crmOpen }
          : item
      )
    );

    const result = await toggleCrmFeature(user.id, !user.crmOpen);

    // Refresh data after API call to ensure consistency
    if (result?.statusCode === 200) {
      _getTableData(true);
    } else {
      // Revert optimistic update on error
      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === user.id
            ? { ...item, crmOpen: !item.crmOpen }
            : item
        )
      );
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const userIds = singleDeleteUser
        ? [singleDeleteUser]
        : selectedRowsData?.selectedRows?.map((user) => user.id);

      const result = await deleteUsers(userIds);

      if (result?.statusCode === 200) {
        // Refresh table after deletion
        _getTableData();
        setSelectedRowsData(null);
      }
    } catch (e) {
      console.error('Delete error:', e);
    } finally {
      setDeleteLoading(false);
      setConfirmationModal(false);
      setSingleDeleteUser(null);
    }
  };

  const openResetPasswordModal = (user) => {
    setUserForReset(user);
    setResetPasswordModalOpen(true);
  };

  const isAdminToggleDisabled = (row) => {
    if (!row.isAdmin) return false;
    return totalUsers === 1 || embedded;
  };

  const handleAdminToggle = async (user) => {
    if (isAdminToggleDisabled(user)) return;

    const nextIsAdmin = !user.isAdmin;

    setTableData((prevData) =>
      prevData.map((item) => {
        if (nextIsAdmin) {
          return { ...item, isAdmin: item.id === user.id };
        }

        if (item.id === user.id) {
          return { ...item, isAdmin: false };
        }

        return item;
      })
    );

    await toggleUserAdmin(user.id, nextIsAdmin);
    _getTableData(true);
  };

  const idColumn = {
    name: 'Id',
    selector: (row) => row.id,
    sortable: true,
    center: false,
    width: '100px',
  };

  const nameColumn = {
    name: 'User Name',
    cell: (row) => {
      const tooltipId = `username-tooltip-${row.id}`;
      return (
        <>
          <div
            id={tooltipId}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.name || '--'}
          </div>
          <UncontrolledTooltip placement="top" target={tooltipId}>
            {row.name || 'N/A'}
          </UncontrolledTooltip>
        </>
      );
    },
    sortable: false,
    center: false,
    width: '180px',
  };

  const emailColumn = {
    name: 'Email',
    cell: (row) => {
      const tooltipId = `email-tooltip-${row.id}`;
      return (
        <>
          <div
            id={tooltipId}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.email}
          </div>
          <UncontrolledTooltip placement="top" target={tooltipId}>
            {row.email}
          </UncontrolledTooltip>
        </>
      );
    },
    sortable: false,
    center: false,
    width: '250px',
  };

  const roleColumn = {
    name: 'Role',
    selector: (row) => row.role || '--',
    sortable: false,
    center: false,
    width: '120px',
  };

  const phoneColumn = {
    name: 'Phone',
    selector: (row) => row.phone || '--',
    sortable: false,
    center: false,
    width: '120px',
  };

  const permissionRoleColumn = {
    name: 'Permission Role',
    selector: (row) => getUserRole(row?.permissions),
    sortable: false,
    center: false,
    width: '150px',
  };

  const lastActivityColumn = {
    name: 'Last Activity',
    selector: (row) => {
      const tooltipId = `last-activity-${row.id}`;
      const engagement = row?.lastEngagement?.[0];
      const value = engagement
        ? `${engagement?.engagement_type} ${(engagement?.target_brand || engagement?.target_retailer)?.name}`
        : '--';
      return (
        <>
          <div
            id={tooltipId}
            style={{
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </div>
          <UncontrolledTooltip placement="top" target={tooltipId}>
            {value}
          </UncontrolledTooltip>
        </>
      );
    },
    sortable: true,
    center: false,
    width: '200px',
  };

  const lastActivityAtColumn = {
    name: 'Last Activity At',
    selector: (row) => {
      const tooltipId = `last-activity-at-${row.id}`;
      const engagement = row?.lastEngagement?.[0];
      const value = engagement ? new Date(engagement?.created_at).toLocaleDateString() : '--';
      return (
        <>
          <div
            id={tooltipId}
            style={{
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </div>
          <UncontrolledTooltip placement="top" target={tooltipId}>
            {value}
          </UncontrolledTooltip>
        </>
      );
    },
    sortable: true,
    center: false,
    width: '200px',
  };

  const lastLoginColumn = {
    name: 'Last Login',
    selector: (row) => (row.lastLogin ? new Date(row.lastLogin).toLocaleString() : '--'),
    sortable: true,
    center: false,
    width: '180px',
  };

  const toggleAdminColumn = {
    name: 'Toggle Admin',
    cell: (row) => {
      const disabled = isAdminToggleDisabled(row);
      const tooltipId = `toggle-admin-tooltip-${row.id}`;

      return (
        <>
          <div className="form-check form-switch">
            <input
              id={tooltipId}
              className="form-check-input"
              type="checkbox"
              checked={row.isAdmin || false}
              disabled={disabled}
              onChange={() => handleAdminToggle(row)}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            />
          </div>
          {disabled && (
            <UncontrolledTooltip placement="top" target={tooltipId}>
              Organization must always have an admin
            </UncontrolledTooltip>
          )}
        </>
      );
    },
    center: true,
    width: '130px',
  };

  const deleteActionColumn = {
    name: 'Actions',
    cell: (row) => (
      <Button
        color="danger"
        size="sm"
        outline
        onClick={() => {
          setSingleDeleteUser(row.id);
          setConfirmationModal(true);
        }}
      >
        <Trash size={14} />
      </Button>
    ),
    center: true,
    width: '100px',
  };

  const organizationColumns = [
    idColumn,
    nameColumn,
    emailColumn,
    roleColumn,
    phoneColumn,
    permissionRoleColumn,
    lastActivityColumn,
    lastActivityAtColumn,
    lastLoginColumn,
    toggleAdminColumn,
    deleteActionColumn,
  ];

  const columns = embedded ? organizationColumns : [
    idColumn,
    nameColumn,
    emailColumn,
    roleColumn,
    phoneColumn,
    {
      name: 'User type',
      selector: (row) => row.type || '--',
      sortable: false,
      center: false,
      width: '120px',
    },
    permissionRoleColumn,
    {
      name: 'Organization',
      cell: (row) => {
        const tooltipId = `orgname-tooltip-${row.id}`;
        return (
          <>
            <div
              id={tooltipId}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.organizationName || '--'}
            </div>
            <UncontrolledTooltip placement="top" target={tooltipId}>
              {row.organizationName || 'N/A'}
            </UncontrolledTooltip>
          </>
        );
      },
      sortable: false,
      center: false,
      width: '200px',
    },
    lastActivityColumn,
    lastActivityAtColumn,
    lastLoginColumn,
    {
      name: 'Joined Date',
      selector: (row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '--',
      sortable: false,
      center: false,
      width: '130px',
    },
    {
      name: 'CRM Active',
      cell: (row) => (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={row.crmOpen || false}
            onChange={() => handleCrmToggle(row)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      ),
      center: true,
      width: '130px',
    },
    {
      name: 'Toggle User',
      cell: (row) => (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={row.active}
            onChange={() => handleUserToggle(row)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      ),
      center: true,
      width: '130px',
    },
    deleteActionColumn,
    {
      name: 'Reset Password',
      cell: (row) => (
        <button
          onClick={() => openResetPasswordModal(row)}
          style={{
            background: 'none',
            border: 'none',
            color: '#007bff',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '14px',
            padding: 0,
          }}
        >
          Reset Password
        </button>
      ),
      center: true,
      width: '140px',
    },
  ];

  const tableContent = (
    <div className="table-responsive mt-3">
      <DataTable
        striped
        pagination
        data={tableData}
        columns={columns}
        className="display"
        paginationServer
        progressPending={isLoading}
        paginationTotalRows={totalUsers}
        paginationPerPage={itemsPerPage}
        paginationDefaultPage={page}
        selectableRows={!embedded}
        onSelectedRowsChange={embedded ? undefined : setSelectedRowsData}
        onChangePage={(newPage) => setPage(newPage)}
        onChangeRowsPerPage={handleItemPerPageSelect}
      />
    </div>
  );

  const confirmationModalElement = (
    <ConfirmationModal
      open={confirmationModal}
      onClose={() => {
        setConfirmationModal(false);
        setSingleDeleteUser(null);
      }}
      onSubmit={handleDelete}
      isLoading={deleteLoading}
      title="Delete User"
      message={
        singleDeleteUser
          ? "Are you sure you want to permanently delete this user from the database? This action cannot be undone and will remove all associated data."
          : `Are you sure you want to permanently delete ${selectedRowsData?.selectedCount || 0} user(s) from the database? This action cannot be undone and will remove all associated data.`
      }
    />
  );

  if (embedded) {
    return (
      <>
        <Card>
          <CardBody>{tableContent}</CardBody>
        </Card>
        {confirmationModalElement}
      </>
    );
  }

  return (
    <Container fluid style={{ padding: "20px 0px" }}>
      <Col sm={12}>
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-between mb-3">
              <Col xs="auto" className="d-flex flex-row gap-3 align-items-center">
                {selectedRowsData?.selectedCount > 0 && (
                  <Button outline color="primary" onClick={() => setConfirmationModal(true)}>
                    <Trash /> Delete ({selectedRowsData.selectedCount})
                  </Button>
                )}
              </Col>
              <Col xs="auto" className="d-flex flex-row gap-3 align-items-center">
                <Label className="mb-0">Search:</Label>
                <Input
                  type="text"
                  placeholder="Search on user name, brand or retailer name / website"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ minWidth: '500px' }}
                />
              </Col>
            </Row>
            {tableContent}
          </CardBody>
        </Card>
      </Col>
      {confirmationModalElement}
      <ResetPasswordModal
        open={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setUserForReset(null);
        }}
        user={userForReset}
        onSubmit={resetUserPassword}
      />
    </Container>
  );
}

const getUserRole = (permissions) => {
  const hasAllAdminViews = (obj) => {
    if (!obj || typeof obj !== 'object') return true;

    for (const [key, value] of Object.entries(obj)) {
      if (key === 'admin_view' && value !== true) {
        return false;
      }

      if (typeof value === 'object' && value !== null) {
        if (!hasAllAdminViews(value)) {
          return false;
        }
      }
    }

    return true;
  };

  const containsAdminView = (obj) => {
    if (!obj || typeof obj !== 'object') return false;

    for (const [key, value] of Object.entries(obj)) {
      if (key === 'admin_view') return true;

      if (typeof value === 'object' && value !== null) {
        if (containsAdminView(value)) return true;
      }
    }

    return false;
  };

  return containsAdminView(permissions) && hasAllAdminViews(permissions)
    ? 'Admin'
    : 'User';
};

const mapStateToProps = (state) => ({
  allUsers: state.userManagement.allUsers,
  totalUsers: state.userManagement.totalUsers,
  isLoading: state.userManagement.isLoading,
});

const mapDispatchToProps = {
  getAllUsers: getAllUsersAction,
  toggleUserStatus: toggleUserStatusAction,
  deleteUsers: deleteUsersAction,
  toggleCrmFeature: toggleCrmFeatureAction,
  resetUserPassword: resetUserPasswordAction,
  toggleUserAdmin: toggleUserAdminAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserManagementUsersTab);
